/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base colors for the Material Dashboard 2 React.
 * You can add new color using this file.
 * You can customized the colors for the entire Material Dashboard 2 React using thie file.
 */

const colors = {
  background: {
    default: "#f0f2f5", // light gray (keep light background)
    paper: "#ffffff",   // white for cards
    sidenav: "#0a1628", // sidenav stays navy
  },

  text: {
    main: "#344767",    // dark gray (normal text)
    focus: "#0a1628",   // darker (focus state)
    secondary: "#7b809a", // gray for secondary text
    disabled: "rgba(0, 0, 0, 0.4)", // faded gray
  },

  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  black: {
    light: "#000000",
    main: "#000000",
    focus: "#000000",
  },

  primary: {
    main: "#f4c542", // gold (primary brand color for headers/buttons)
    focus: "#f39c12", // darker gold
  },

  secondary: {
    main: "#7b809a", // gray for light background
    focus: "#344767", // darker gray
  },

  info: {
    main: "#0d1628", // navy (unified theme)
    focus: "#0a1628", // darker navy
  },

  success: {
    main: "#4CAF50",
    focus: "#67bb6a",
  },

  warning: {
    main: "#f39c12", // orange-gold
    focus: "#e67e22", // darker orange
  },

  error: {
    main: "#F44335",
    focus: "#f65f53",
  },

  light: {
    main: "#f0f2f5", // light gray
    focus: "#e9ecef", // slightly darker light gray
  },

  dark: {
    main: "#344767", // dark gray
    focus: "#2c3c58", // darker gray
  },

  grey: {
    100: "#f8f9fa",
    200: "#f0f2f5",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#6c757d",
    700: "#495057",
    800: "#343a40",
    900: "#212529",
  },

  gradients: {
    primary: {
      main: "#f4c542", // gold
      state: "#f39c12", // darker gold
    },

    secondary: {
      main: "#1a2942", // medium navy
      state: "#0d1628", // dark navy
    },

    info: {
      main: "#0d1628", // navy
      state: "#0a1628", // darker navy
    },

    success: {
      main: "#00a020", // green (keep semantic)
      state: "#00630f", // darker green
    },

    warning: {
      main: "#f39c12", // orange-gold
      state: "#e67e22", // darker orange
    },

    error: {
      main: "#a00000", // red (keep semantic)
      state: "#630000", // darker red
    },

    light: {
      main: "#1a2942", // medium navy
      state: "#152138", // darker navy
    },

    dark: {
      main: "#0a1628", // dark navy
      state: "#050b14", // darkest navy
    },
  },

  socialMediaColors: {
    facebook: {
      main: "#3b5998",
      dark: "#344e86",
    },

    twitter: {
      main: "#55acee",
      dark: "#3ea1ec",
    },

    instagram: {
      main: "#125688",
      dark: "#0e456d",
    },

    linkedin: {
      main: "#0077b5",
      dark: "#00669c",
    },

    pinterest: {
      main: "#cc2127",
      dark: "#b21d22",
    },

    youtube: {
      main: "#e52d27",
      dark: "#d41f1a",
    },

    vimeo: {
      main: "#1ab7ea",
      dark: "#13a3d2",
    },

    slack: {
      main: "#3aaf85",
      dark: "#329874",
    },

    dribbble: {
      main: "#ea4c89",
      dark: "#e73177",
    },

    github: {
      main: "#24292e",
      dark: "#171a1d",
    },

    reddit: {
      main: "#ff4500",
      dark: "#e03d00",
    },

    tumblr: {
      main: "#35465c",
      dark: "#2a3749",
    },
  },

  badgeColors: {
    primary: {
      background: "#f8b3ca",
      text: "#cc084b",
    },

    secondary: {
      background: "#d7d9e1",
      text: "#6c757d",
    },

    info: {
      background: "#aecef7",
      text: "#095bc6",
    },

    success: {
      background: "#bce2be",
      text: "#339537",
    },

    warning: {
      background: "#ffd59f",
      text: "#c87000",
    },

    error: {
      background: "#fcd3d0",
      text: "#f61200",
    },

    light: {
      background: "#ffffff",
      text: "#c7d3de",
    },

    dark: {
      background: "#8097bf",
      text: "#1e2e4a",
    },
  },

  coloredShadows: {
    primary: "#e91e62",
    secondary: "#110e0e",
    info: "#00bbd4",
    success: "#4caf4f",
    warning: "#ff9900",
    error: "#f44336",
    light: "#adb5bd",
    dark: "#404040",
  },

  inputBorderColor: "#d2d6da",

  tabs: {
    indicator: { boxShadow: "#ddd" },
  },

  // STAIL Brand Colors (Added November 6, 2025)
  // Frequently used colors throughout the application
  brandColors: {
    // Primary brand colors
    gold: "#dca716",
    goldLight: "#ffd359",
    goldDark: "#836000",
    
    // Navy/Blue tones
    navyBlue: "#0d1a4d",
    navyBlueDark: "#102040",
    
    // Neutral backgrounds
    lightGrey: "#fafafa",
    lightGreyAlt: "#f8f9fa",
    
    // Text colors
    textPrimary: "#5b5e70",
    
    // Semantic colors (from gradients)
    brown: "rgba(103, 60, 0, 1)",
    brownOrange: "rgba(255, 170, 51, 1)",
    pink: "rgb(179, 0, 87)",
    magenta: "rgb(248, 81, 167)",
    blueDeep: "rgba(0, 39, 116, 1)",
    blueBright: "rgba(0, 123, 255, 1)",
    bluePrimary: "#1565c0",
    greenDark: "rgba(0, 99, 15, 1)",
    greenBright: "rgba(0, 206, 31, 1)",
    redDark: "rgba(99, 0, 0, 1)",
    redBright: "rgba(206, 0, 0, 1)",
  },
};

export default colors;
